import React from "react";
import { useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../store/state/FilterLoadingState";
import { EmptyState } from "../recharts/ReMixCharts/components/EmptyState";
import "../recharts/ReMixCharts/ReMixChart.css";
import { useEmptyStateVisibility } from "../recharts/ReMixCharts/hooks/useEmptyStateVisibility";
import { LegendSummaryTableProps } from "./types";
import { calculateLegendStats } from "./calculateLegendStats";

/**
 * A component that displays a summary table of legend statistics.
 * In addition to the regular columns (Min, Max, Avg, Total), if priceLegends are provided,
 * the Min and Max groups will each display two main columns: one for the "Mu" (value)
 * and one for "Price", followed by extra sub-columns.
 *
 * For example, if extraKeys is ["time_block","date"], the sub-headers for the Min group will be:
 *   Mu | Price | Time Block | Date
 *
 * The component computes the price values by scanning the data for the corresponding price legend key
 * (matched by the same index in the priceLegends array).
 */
const LegendSummaryTable: React.FC<LegendSummaryTableProps> = ({
  legends,
  data,
  unit = "MWh",
  extraKeys,
  tableData = [],
  extraLabels,
  block_key,
  priceLegends,
  showTotal = true,
  showWtdAvg = false,
  showTotalEmpty = false,
  isLoading,
  emptyStateDelayMs = 450,
}) => {
  // Pair each extra key with its label.
  const extras = extraKeys.map((key, idx) => ({
    key,
    label: extraLabels[idx] || key,
  }));
  const dispatch = useDispatch();

  // Calculate basic stats for the legends.
  const stats = calculateLegendStats(
    unit,
    legends,
    data,
    extraKeys,
    tableData,
    showWtdAvg,
  );

  // If priceLegends are provided, compute minPrice and maxPrice for each legend.
  const statsWithPrice = stats.map((stat, index) => {
    if (priceLegends && priceLegends[index]) {
      const priceKey = priceLegends[index].dataKey;
      let minPrice = Infinity;
      let maxPrice = -Infinity;
      data.forEach((item) => {
        const price = Number(item[priceKey]);
        if (!isNaN(price)) {
          if (price < minPrice) minPrice = price;
          if (price > maxPrice) maxPrice = price;
        }
      });

      return {
        ...stat,
        minPrice: minPrice === Infinity ? 0 : minPrice,
        maxPrice: maxPrice === -Infinity ? 0 : maxPrice,
      };
    }

    dispatch(setLoadingFilterName(""));
    return stat;
  });

  // Adjust total if unit is 'mw': convert total to MWh by dividing by 4.

  const adjustedStats = statsWithPrice.map((stat) => ({
    ...stat,
  }));

  const showEmpty = useEmptyStateVisibility({
    isEmpty: adjustedStats.length === 0,
    isLoading,
    delayMs: emptyStateDelayMs,
  });

  // Determine the label for numeric values.
  const valueUnitLabel = unit === "MW" ? "MW" : unit === "MWh" ? "MWh" : unit;

  // Determine how many sub-columns each group should have.
  // If priceLegends exist, then for each group (Min/Max) we have: "Mu", "Price", plus the extra columns.
  const groupColumnCount = (priceLegends ? 2 : 1) + extras.length;

  return (
    <div className="overflow-x-auto capitalize tab-animatable-content relative flex flex-col h-full min-h-[300px]">
      <table className="min-w-full text-nowrap ">
        <thead className="sticky top-0 z-10 ">
          {/* First header row: group labels */}
          <tr className="relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-[#fdd3bb]">
            <th
              rowSpan={2}
              className="py-2 px-6 text-center z-10 relative after:content-[''] border-0 after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Name
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Minimum
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Maximum
            </th>
            <th
              rowSpan={2}
              className={`py-2 px-6 text-center z-10 relative border-0 ${
                showTotal === false && showWtdAvg === false
                  ? ""
                  : "after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              }`}
            >
              Average
            </th>
            {showTotal && (
              <th rowSpan={2} className="py-2 px-6 z-10 border-0 text-center">
                Total
              </th>
            )}
            {showWtdAvg && (
              <th rowSpan={2} className="py-2 px-6 z-10 border-0 text-center">
                Wt. Avg
              </th>
            )}
          </tr>
          {/* Second header row: sub-columns */}
          <tr className="text-gray-700">
            {/* Min group sub-columns */}
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {priceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceLegends[0].unit}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`min-extra-header-${idx}`}
                className="py-2 px-6 text-center capitalize border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
            {/* Max group sub-columns */}
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {priceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceLegends[0].unit}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`max-extra-header-${idx}`}
                className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-[#8C8A96] font-normal text-[13px]">
          {adjustedStats.map((stat, index) => (
            <tr
              key={index}
              className="border-b rounded-xl border-gray-200 hover:bg-gray-100 text-center"
            >
              <td className="py-3 px-6 text-center ">
                {(() => {
                  if (/\(forecast\)\s*$/i.test(stat.name)) {
                    return stat.name;
                  }
                  // Parse the name to find unit in parentheses
                  const match = stat.name.match(/(.*)\s*\((.*?)\)$/);
                  if (match) {
                    const [_, nameText, unitText] = match;
                    return (
                      <>
                        <span>{nameText}</span>{" "}
                        <span className="text-[#34656D] font-medium">
                          ({unitText})
                        </span>
                      </>
                    );
                  } else {
                    // If no unit pattern found, just return the whole name
                    return stat.name;
                  }
                })()}
              </td>
              {/* Min group: Mu column */}
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.min.toFixed(2)}
              </td>
              {/* Min group: Price column */}
              {priceLegends && (
                <td className="py-3 px-6 ">
                  {stat.minPrice !== undefined ? stat.minPrice.toFixed(2) : "-"}
                </td>
              )}
              {/* Min group: Extra columns */}
              {extras.map((extra, idx) => {
                return (
                  <td
                    key={`min-extra-${index}-${idx}`}
                    className={`py-3 px-6 ${
                      extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                    }`}
                  >
                    {stat.minExtra[extra.key] !== undefined
                      ? stat.minExtra[extra.key]
                      : "-"}
                  </td>
                );
              })}
              {/* Max group: Mu column */}
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.max.toFixed(2)}
              </td>
              {/* Max group: Price column */}
              {priceLegends && (
                <td className="py-3 px-6">
                  {stat.maxPrice !== undefined ? stat.maxPrice.toFixed(2) : "-"}
                </td>
              )}
              {/* Max group: Extra columns */}
              {extras.map((extra, idx) => (
                <td
                  key={`min-extra-${index}-${idx}`}
                  className={`py-3 px-6 ${
                    extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                  }`}
                >
                  {stat.maxExtra[extra.key] !== undefined
                    ? stat.maxExtra[extra.key]
                    : "-"}
                </td>
              ))}
              <td className="py-3 px-6">
                <span className=" font-medium">{stat.avg.toFixed(2)}</span>
              </td>
              {showTotal && (
                <td className="py-3 px-6">
                  {(() => {
                    const match = stat.name.match(/.*\((.*?)\)$/);
                    let unit = stat.unit || (match ? match[1] : "");

                    const WEATHER_UNITS = ["°C", "km/h", "mm", "s", "W/m²"];
                    // If it's a percentage, rate (contains Rs), weather unit, or the total is NaN, show "-"
                    if (
                      isNaN(stat.total) ||
                      unit === "%" ||
                      unit.includes("Rs") ||
                      unit.includes("₹") ||
                      stat.name.includes("%") ||
                      stat.name.toLowerCase().includes("percent") ||
                      WEATHER_UNITS.includes(unit)
                    ) {
                      return <span>-</span>;
                    }

                    if (unit.toLowerCase() === "mw") {
                      unit = "MWh";
                    }
                    return (
                      <span className="font-medium">
                        {showTotalEmpty ? (
                          <span>-</span>
                        ) : (
                          <>
                            {" "}
                            {stat.total.toFixed(2)} {" ("}
                            {unit}
                            {")"}
                          </>
                        )}
                      </span>
                    );
                  })()}
                </td>
              )}
              {showWtdAvg && (
                <td className="py-3 px-6">
                  <span className="font-medium">
                    {stat.wtdAvg === null
                      ? "-"
                      : stat.wtdAvg !== undefined
                        ? stat.wtdAvg.toFixed(2)
                        : "-"}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {adjustedStats.length === 0 && showEmpty && (
        <div className="relative w-full flex-1 min-h-[280px] grow self-stretch">
          <EmptyState />
        </div>
      )}
      {priceLegends && (
        <div className="text-center text-sm text-gray-500 mt-4 ">
          The Min and Max {priceLegends[0].unit} is corresponding to the Min and
          Max {extraKeys.join(", ")} of {valueUnitLabel}
        </div>
      )}
    </div>
  );
};

export default LegendSummaryTable;
